/**
 * Hooks local-first de TUTORÍAS (M3): cola de corrección, historial de versiones
 * y config de SLA/reentrega (para gatear la UI). Sobre los streams por rol:
 * `submission_version` viaja con my_cohorts_teacher / my_cohorts_student /
 * org_admin_scope; la config con academy_org.
 */
import type { Accessor } from "solid-js";
import {
  getReviewPolicyQuery,
  getReviewQueueQuery,
  getSubmissionVersionsQuery,
  getTutoringSettingsQuery,
  tutoringQueryKeys,
} from "@space/shared";
import { createPowerSyncQuery } from "@space/shared/solid";
import {
  type Assignment,
  type AssignmentReviewPolicy,
  assignment as assignmentTable,
  type SubmissionVersion,
  type TutoringSettings,
  type User,
  user as userTable,
} from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  return { db, drizzle, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** Cola de corrección: versiones pendientes (outcome IS NULL) de mis cohortes. */
export function useReviewQueue(cohortId?: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<SubmissionVersion>({
    queryKey: () => tutoringQueryKeys.queue(cohortId?.()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      return db ? getReviewQueueQuery(db, cohortId?.()) : null;
    },
    enabled: () => Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Historial de versiones de una entrega (asc por versión). */
export function useSubmissionVersions(
  assignmentId: Accessor<string | undefined>,
  cohortId: Accessor<string | undefined>,
  userId: Accessor<string | undefined>,
) {
  const base = useBase();
  return createPowerSyncQuery<SubmissionVersion>({
    queryKey: () => tutoringQueryKeys.versions(assignmentId(), cohortId(), userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const a = assignmentId();
      const c = cohortId();
      const u = userId();
      return db && a && c && u ? getSubmissionVersionsQuery(db, a, c, u) : null;
    },
    enabled: () =>
      Boolean(assignmentId()) &&
      Boolean(cohortId()) &&
      Boolean(userId()) &&
      Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Config de tutorías por org (fila única; puede no existir → fallback). */
export function useTutoringSettings(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<TutoringSettings>({
    queryKey: () => tutoringQueryKeys.settings(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const o = orgId();
      return db && o ? getTutoringSettingsQuery(db, o) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Todas las tareas locales (índice id→título para la cola). */
export function useAllAssignments() {
  const base = useBase();
  return createPowerSyncQuery<Assignment>({
    queryKey: () => tutoringQueryKeys.assignmentsIndex,
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      return db ? db.select().from(assignmentTable) : null;
    },
    enabled: () => Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Identidades del roster local (índice id→nombre para la cola). */
export function useRosterUsers() {
  const base = useBase();
  return createPowerSyncQuery<User>({
    queryKey: () => tutoringQueryKeys.usersIndex,
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      return db ? db.select().from(userTable) : null;
    },
    enabled: () => Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Override de política por tarea (puede no existir → hereda de la org). */
export function useReviewPolicy(assignmentId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<AssignmentReviewPolicy>({
    queryKey: () => tutoringQueryKeys.reviewPolicy(assignmentId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const a = assignmentId();
      return db && a ? getReviewPolicyQuery(db, a) : null;
    },
    enabled: () => Boolean(assignmentId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}
